import { SEOHead } from '@/components/SEOHead';
import { PageMasthead } from '@/components/ui/PageMasthead';
import { PageNav } from '@/components/ui/PageNav';
import { MobileSectionNav } from '@/components/ui/MobileSectionNav';
import { useSectionSpy } from '@/hooks/useSectionSpy';
import {
  FigureIntervalResidual,
  FigureStressWindows,
  EvidencePlate,
  MemoEyebrow,
  SectionNum,
  CtaPrimary,
  CtaGhost,
} from '@/components/Patterns';

/* ── Content (COPY-LOCKED — do not alter any visible sentence) ── */

const STAT_ROWS = [
  { label: 'Timeline', value: '3 weeks' },
  { label: 'Input Format', value: 'CSV or XLSX' },
  { label: 'Output Format', value: 'PDF memorandum + annexes' },
];

const REQUIRED_COLUMNS = [
  'Date',
  'Interval Start',
  'Queue / Skill ID',
  'Offered Calls',
  'Handled Calls',
  'Abandoned Calls',
  'AHT (avg)',
  'Service Level %',
  'Occupancy %',
  'Agents Scheduled',
  'Agents Staffed',
];

const OPTIONAL_COLUMNS = [
  'Adherence %',
  'Shrinkage Log',
  'Reforecast Timestamps',
  'Skill Routing Rules',
  'Queue Overflow Events',
];

const INPUT_SPEC = [
  { label: 'Minimum history required', value: '60 days' },
  { label: 'Interval granularity', value: '15 or 30 minutes' },
  { label: 'File format', value: 'CSV or XLSX' },
];

const DELIVERABLES = [
  {
    num: '01',
    ref: 'Memo',
    title: 'Executive Memorandum',
    desc: '2-4 page structured PDF summary. Key distortion domain(s) identified. Secondary causes of interaction (max 3) and signal characteristics defined. Possible next step: boundary cases.',
  },
  {
    num: 'A1',
    ref: 'Annex A',
    title: 'Interval Analysis Tables',
    desc: 'Presentation of interval level classification results in table format with domain scores per queue and time interval. Number of recurrences and sensitivity index.',
  },
  {
    num: 'B1',
    ref: 'Annex B',
    title: 'Signal Structure Charts',
    desc: 'PDF embedded visualization of instability signal signatures. Heatmap per residual line and exposure map as classified outputs.',
  },
  {
    num: 'C1',
    ref: 'Annex C',
    title: 'Data Sufficiency Assessment',
    desc: 'Evaluation of submitted data against classification requirements with highlighted gaps. Precise specification of export for reengagement & following modules.',
  },
];

const MODULES = [
  { name: 'Extended Forecast Decomposition', desc: 'Deep dive into separating out volume and pattern components across longer timeframes.' },
  { name: 'Multiqueue Topology Mapping', desc: 'Cross queue routing and analysis of overflow from a multi skill perspective.' },
  { name: 'Adherence Attribution Audit', desc: 'Systematic recategorization of adherence states by actual behavior patterns.' },
  { name: 'Shrinkage Recalibration', desc: 'Shrinkage category remapping & quantification of historical errors.' },
  { name: 'Intraday Response Latency Analysis', desc: 'Characterization of management response relative to signal emergence.' },
  { name: 'Data Sufficiency Roadmap', desc: 'Specification of extra data capture requirements to enable finer classification.' },
];

/* ── Rapid Diagnostic rail — concise nav labels + section ids ── */

const RAPID_RAIL = [
  { id: 'rapid-scope-inputs', num: '01', label: 'Scope & Inputs' },
  { id: 'rapid-deliverables', num: '02', label: 'Deliverables' },
  { id: 'rapid-modules', num: '03', label: 'Optional Modules' },
  { id: 'rapid-pricing', num: '04', label: 'Pricing' },
];

const RAPID_IDS = RAPID_RAIL.map((s) => s.id);

/* Hairline-ruled inline mono tag (non-pill) for required/optional columns */
const ColTag = ({ label, optional }) => (
  <span
    className={`inline-block border-b font-mono text-xs tracking-[0.02em] px-2 py-1 leading-none ${optional ? 'border-memo-line text-memo-muted' : 'border-memo-line-2 text-memo-ink-2'}`}
  >
    {label}
  </span>
);

/* A single ruled ledger cell (label above value) used across stat rows */
const LedgerCell = ({ label, value, emph }) => (
  <div className="flex flex-col justify-end py-5">
    <span className="font-mono text-xs uppercase tracking-[0.1em] text-memo-muted">{label}</span>
    <span className={`mt-1.5 font-display text-lg font-medium ${emph ? 'text-memo-ink' : 'text-memo-ink-2'}`} style={{ color: emph ? 'var(--memo-ink)' : 'var(--memo-ink-2)' }}>
      {value}
    </span>
  </div>
);

export function RapidDiagnostic() {
  const activeId = useSectionSpy(RAPID_IDS, '0px 0px -80% 0px');

  return (
    <div style={{ backgroundColor: 'var(--memo-field)' }}>
      <SEOHead />

      {/* ── Compact masthead — engagement identity ── */}
      <PageMasthead
        wide
        field="quiet"
        fieldFocal="0.76,0.22"
        eyebrow="Primary Engagement"
        title="OnPoint Diagnostics"
        intro="A bounded, file-based structural instability classification engagement with fixed scope and fixed output. Completed in 3 weeks from the Pre Diagnostics Session."
      />

      {/* The three engagement constants — hairline-ruled ledger, not cards */}
      <div className="container-site spine-shell border-b border-memo-line">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-memo-line">
          {STAT_ROWS.map((s, i) => (
            <div key={s.label} className={`py-5 ${i > 0 ? 'md:border-l md:pl-6' : ''}`}>
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-mono text-xs uppercase tracking-[0.14em] text-memo-muted">{s.label}</span>
                <span className="font-display text-lg font-medium text-right" style={{ color: 'var(--memo-ink)' }}>{s.value}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Scope + canvas (lg: nav | content) ── */}
      <div className="container-site spine-shell lg:grid lg:grid-cols-[16rem_minmax(0,1fr)]">
        <PageNav items={RAPID_RAIL} activeId={activeId} label="Rapid diagnostic sections" />

        <div className="lg:min-w-0 lg:border-l lg:border-memo-line lg:pl-10">
          <MobileSectionNav items={RAPID_RAIL} activeId={activeId} label="Rapid diagnostic sections" />

      {/* ── Scope + Input Requirements — split memorandum layout ── */}
      <section id="rapid-scope-inputs" className="py-16 md:py-20 bg-memo-paper border-b border-memo-line">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          {/* Engagement Scope */}
          <div>
            <h3 className="font-display text-2xl font-medium tracking-tight">Engagement Scope</h3>
            <div className="mt-6 space-y-5">
              <p className="text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
                OnPoint Diagnostics characterizes structural distortion within one workforce system environment: one export request, one classification process, and one set of deliverables.
              </p>
              <p className="text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
                The bounds do not increase during engagement. If the submitted data indicates additional dimensions requiring analysis, these are captured as bounded next steps and are not included in the active engagement.
              </p>
              <p className="text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
                For multi-site or multi-environment organizations, scope is defined independently for each unit, and each environment is exported and analyzed separately.
              </p>
            </div>

            {/* Data retention */}
            <div className="mt-10 p-5 rounded" style={{ border: '1px solid var(--memo-warn)', backgroundColor: 'var(--memo-warn-bg)' }}>
              <div className="font-display text-sm font-medium" style={{ color: 'var(--memo-warn)' }}>Data Retention Policy</div>
              <p className="mt-2.5 text-sm leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
                All processed data is deleted 14 days after engagement completion.
              </p>
            </div>
          </div>

          {/* Input Requirements */}
          <div>
            <h3 className="font-display text-2xl font-medium tracking-tight">Input Requirements</h3>

            <div className="mt-7">
              <p className="text-xs text-memo-muted font-medium mb-3">Required columns</p>
              <div className="flex flex-wrap gap-x-2.5 gap-y-2.5">
                {REQUIRED_COLUMNS.map((col) => <ColTag key={col} label={col} />)}
              </div>
            </div>

            <div className="mt-8">
              <p className="text-xs text-memo-muted font-medium mb-3">Optional (increases classification depth)</p>
              <div className="flex flex-wrap gap-x-2.5 gap-y-2.5">
                {OPTIONAL_COLUMNS.map((col) => <ColTag key={col} label={col} optional />)}
              </div>
            </div>

            {/* Data spec ledger */}
            <div className="mt-8 border-t border-memo-line">
              {INPUT_SPEC.map((row) => (
                <div key={row.label} className="flex items-center justify-between py-3 border-b border-memo-line">
                  <span className="text-xs text-memo-muted">{row.label}</span>
                  <span className="font-mono text-xs text-memo-ink-2">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Output Deliverables — full-width memorandum frames ── */}
      <section id="rapid-deliverables" className="py-16 md:py-20 border-b border-memo-line">
        <div>
          <div className="mb-12">
            <MemoEyebrow>Deliverables</MemoEyebrow>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3">Output Deliverables</h2>
          </div>

          <div className="flex flex-col gap-0 border-t border-memo-line-2">
            {DELIVERABLES.map((d) => (
              <div
                key={d.ref}
                data-testid={`deliverable-${d.ref.toLowerCase().replace(/\s+/g, '-')}`}
                className="grid grid-cols-[3rem_1fr] lg:grid-cols-[4rem_1fr] gap-6 py-7 border-b border-memo-line"
              >
                <SectionNum>{d.num}</SectionNum>
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs tracking-[0.14em] uppercase text-memo-signal border border-memo-line px-2 py-0.5 rounded">{d.ref}</span>
                    <h3 className="font-display text-xl font-medium">{d.title}</h3>
                  </div>
                  <p className="mt-3 text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>{d.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Evidence — the analysis type named in the annex copy */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
            <EvidencePlate tag="Evidence · interval residual">
              <FigureIntervalResidual />
            </EvidencePlate>
            <EvidencePlate tag="Evidence · recurring stress windows">
              <FigureStressWindows />
            </EvidencePlate>
          </div>
        </div>
      </section>

      {/* ── Optional Analysis Modules — narrow ruled list ── */}
      <section id="rapid-modules" className="py-16 md:py-20 bg-memo-paper border-b border-memo-line">
        <div className="max-w-[66ch]">
          <MemoEyebrow>Optional Modules</MemoEyebrow>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3">Optional Analysis Modules</h2>
          <p className="mt-4 text-lg leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
            Available for scoping upon completion of OnPoint Diagnostics. Does not roll into the base engagement.
          </p>

          <ol className="mt-10 flex flex-col gap-0 border-t border-memo-line">
            {MODULES.map((mod, i) => (
              <li key={mod.name} className="grid grid-cols-[3.5rem_1fr] gap-5 py-6 border-b border-memo-line">
                <SectionNum>{String(i + 1).padStart(2, '0')}</SectionNum>
                <div>
                  <h3 className="font-display text-lg font-medium" style={{ color: 'var(--memo-ink)' }}>{mod.name}</h3>
                  <p className="mt-1.5 text-[0.9rem] leading-relaxed text-memo-muted">{mod.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Pricing — hairline-ruled ledger rows, not cards ── */}
      <section id="rapid-pricing" className="py-16 md:py-20 border-b border-memo-line">
        <div>
          <div className="mb-10">
            <MemoEyebrow>Fee Schedule</MemoEyebrow>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3">Pricing</h2>
          </div>

          {/* Pre Diagnostics Session */}
          <div className="border-t border-memo-line-2">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-0 items-start py-6">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.14em] text-memo-signal">INTAKE</p>
                <h3 className="font-display text-2xl font-medium mt-1.5">Pre Diagnostics Session</h3>
              </div>
              <div className="text-right md:text-left">
                <div className="font-display text-3xl font-medium" style={{ color: 'var(--memo-ink)' }}>$250</div>
                <div className="text-xs text-memo-muted mt-1">fixed fee</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-memo-line">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.1em] text-memo-muted mb-3">Included</p>
                {[
                  '60 - 90 minute live session',
                  'Environment mapping (queues, skills, SLAs)',
                  'Structural risk screening',
                  'Precise export checklist',
                  'Written summary of the session',
                ].map((item) => (
                  <p key={item} className="flex items-start gap-2.5 border-t border-memo-line py-2.5">
                    <span className="text-memo-signal" aria-hidden="true">—</span>
                    <span className="text-sm leading-snug" style={{ color: 'var(--memo-ink-2)' }}>{item}</span>
                  </p>
                ))}
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.1em] text-memo-muted mb-3">Not included</p>
                {[
                  'Full diagnostics analysis cycle',
                  'Annex artifacts (A/B/C)',
                  'Implementation or tool configuration',
                ].map((item) => (
                  <p key={item} className="flex items-start gap-2.5 border-t border-memo-line py-2.5">
                    <span className="text-memo-faint" aria-hidden="true">·</span>
                    <span className="text-sm leading-snug text-memo-muted">{item}</span>
                  </p>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-memo-line mt-4">
              {[
                { label: 'Timeline', value: '3-5 business days' },
                { label: 'Format', value: 'Remote session' },
                { label: 'Payment terms', value: '100% to book' },
                { label: 'Credit', value: 'Not automatically applied to Diagnostics' },
              ].map((s) => <LedgerCell key={s.label} {...s} />)}
            </div>
          </div>

          {/* OnPoint Diagnostics */}
          <div className="border-t border-memo-line-2 mt-14">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-0 items-start py-6">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.14em] text-memo-signal">Base Engagement</p>
                <h3 className="font-display text-2xl font-medium mt-1.5">OnPoint Diagnostics</h3>
              </div>
              <div className="text-right md:text-left">
                <div className="font-display text-2xl font-medium" style={{ color: 'var(--memo-ink)' }}>Defined via Intake</div>
                <div className="text-xs text-memo-muted mt-1">per engagement</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-memo-line">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.1em] text-memo-muted mb-3">Included</p>
                {[
                  'Executive memorandum (2-4 pages)',
                  'Annex A: Interval analysis tables',
                  'Annex B: Signal structure charts',
                  'Annex C: Data sufficiency assessment',
                  'One environment & one classification cycle',
                  '14 days data retention',
                ].map((item) => (
                  <p key={item} className="flex items-start gap-2.5 border-t border-memo-line py-2.5">
                    <span className="text-memo-signal" aria-hidden="true">—</span>
                    <span className="text-sm leading-snug" style={{ color: 'var(--memo-ink-2)' }}>{item}</span>
                  </p>
                ))}
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.1em] text-memo-muted mb-3">Not included</p>
                {[
                  'Implementation support',
                  'Platform configuration',
                  'Ongoing monitoring',
                  'Multi environment scope',
                  'Optional analysis modules',
                ].map((item) => (
                  <p key={item} className="flex items-start gap-2.5 border-t border-memo-line py-2.5">
                    <span className="text-memo-faint" aria-hidden="true">·</span>
                    <span className="text-sm leading-snug text-memo-muted">{item}</span>
                  </p>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-memo-line mt-4">
              {[
                { label: 'Timeline', value: '3 weeks' },
                { label: 'Data retention', value: '14 days' },
                { label: 'Payment terms', value: '100% on engagement start' },
                { label: 'Invoice format', value: 'PDF / wire / ACH' },
              ].map((s) => <LedgerCell key={s.label} {...s} />)}
            </div>
          </div>

          {/* Optional Module Pricing */}
          <div className="mt-16 border-t border-memo-line-2 py-7">
            <p className="font-mono text-xs uppercase tracking-[0.14em] text-memo-muted mb-3">Optional Module Pricing</p>
            <p className="text-[0.95rem] leading-relaxed max-w-[66ch]" style={{ color: 'var(--memo-ink-2)' }}>
              Optional analysis modules (A - F) are scoped and priced separately once OnPoint Diagnostics is completed. Module cost reflects data complexity, environment size, and the level of detail required. Module scope will be proposed in writing prior to any work.
            </p>
          </div>

          {/* Enterprise & Multi-Site Pricing */}
          <div className="mt-8 border-t border-memo-line py-7">
            <p className="font-mono text-xs uppercase tracking-[0.14em] text-memo-signal mb-3">Enterprise &amp; Multi-Site Pricing</p>
            <p className="text-[0.95rem] leading-relaxed max-w-[66ch]" style={{ color: 'var(--memo-ink-2)' }}>
              Organizations that need classification across many environments, regions, or business units are scoped separately. Contact us for details on multi-environment scope and contracting.
            </p>
          </div>
        </div>
      </section>
        </div>
      </div>

      {/* ── CTA — memorandum sign-off ── */}
      <section className="py-20 md:py-28 bg-memo-paper-2 border-t border-memo-line">
        <div className="container-memo">
          <div className="flex flex-wrap gap-3">
            <CtaPrimary to="/intake" testid="rapid-diag-intake-cta">Begin Intake Protocol</CtaPrimary>
            <CtaGhost to="/diagnostic-preview">Preview Output Format</CtaGhost>
          </div>
        </div>
      </section>
    </div>
  );
}